import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { NumberInput } from '../ui/number-input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import { WodMovement, WodElementType } from '../../types/wod.types';
import { v4 as uuidv4 } from 'uuid';

interface MovementDialogProps {
  open: boolean;
  movement?: WodMovement;
  onClose: () => void;
  onSave: (movement: WodMovement) => void;
  disableTiebreak?: boolean;
}

export const MovementDialog: React.FC<MovementDialogProps> = ({
  open,
  movement,
  onClose,
  onSave,
  disableTiebreak = false
}) => {
  const { t } = useTranslation();
  const [description, setDescription] = useState('');
  const [reps, setReps] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [isTiebreak, setIsTiebreak] = useState(false);
  const [isMaxReps, setIsMaxReps] = useState(false);
  const [weightKg, setWeightKg] = useState<number | ''>('');
  const [percentageRm, setPercentageRm] = useState<number | ''>('');
  const [rmReference, setRmReference] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (movement) {
      setDescription(movement.description || '');
      setReps(movement.reps || '');
      setNotes(movement.notes || '');
      setIsTiebreak(movement.isTiebreak || false);
      setIsMaxReps(movement.isMaxReps || false);
      setWeightKg(movement.weight_kg ?? '');
      setPercentageRm(movement.percentage_rm ?? '');
      setRmReference(movement.rm_reference || '');
    } else {
      setDescription('');
      setReps('');
      setNotes('');
      setIsTiebreak(false);
      setIsMaxReps(false);
      setWeightKg('');
      setPercentageRm('');
      setRmReference('');
    }
    setErrors({});
  }, [movement, open]);

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!description.trim()) {
      newErrors.description = t('wods.movementDialog.descriptionRequired');
    }

    if (reps !== '' && (reps < 1 || !Number.isInteger(reps))) {
      newErrors.reps = t('wods.movementDialog.repsInvalid');
    }
    if (percentageRm !== '' && (percentageRm <= 0 || percentageRm > 200)) {
      newErrors.percentageRm = t('wods.movementDialog.percentRmInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    // `...movement` : un mouvement généré par WODEE peut porter des champs que
    // ce dialogue n'édite pas (weight_kg_women…) — les reconstruire à partir
    // des seuls champs du formulaire les perdait à la première édition.
    const newMovement: WodMovement = {
      ...movement,
      id: movement?.id || uuidv4(),
      type: WodElementType.MOVEMENT,
      description: description.trim(),
      // Max reps : pas de reps prescrites, le nombre est le score.
      reps: !isMaxReps && reps !== '' ? Number(reps) : undefined,
      isMaxReps: isMaxReps || undefined,
      notes: notes.trim() || undefined,
      weight_kg: weightKg !== '' && Number(weightKg) > 0 ? Number(weightKg) : undefined,
      percentage_rm: percentageRm !== '' && Number(percentageRm) > 0 ? Number(percentageRm) : undefined,
      rm_reference: percentageRm !== '' && rmReference.trim() ? rmReference.trim() : undefined,
      isTiebreak: isTiebreak || undefined
    };

    onSave(newMovement);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {movement ? t('wods.movementDialog.titleEdit') : t('wods.movementDialog.titleAdd')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Description du mouvement */}
          <div className="space-y-2">
            <Label htmlFor="movement_description">
              {t('wods.movementDialog.description')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="movement_description"
              placeholder={t('wods.movementDialog.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={errors.description ? 'border-destructive' : ''}
              autoFocus
            />
            <p className={`text-xs ${errors.description ? 'text-destructive' : 'text-muted-foreground'}`}>
              {errors.description || t('wods.movementDialog.descriptionLabel')}
            </p>
          </div>

          {/* Nombre de répétitions — ou « max reps » : le nombre est le score */}
          <div className="space-y-2">
            <Label htmlFor="movement_reps">{t('wods.movementDialog.reps')}</Label>
            <NumberInput
              id="movement_reps"
              placeholder="Optionnel"
              value={isMaxReps ? '' : reps}
              disabled={isMaxReps}
              onChange={(e) => setReps(e.target.value === '' ? '' : Number(e.target.value))}
              className={errors.reps ? 'border-destructive' : ''}
              min={1}
              step={1}
            />
            <p className={`text-xs ${errors.reps ? 'text-destructive' : 'text-muted-foreground'}`}>
              {errors.reps || t('wods.movementDialog.repsHelp')}
            </p>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="movement_max_reps"
                checked={isMaxReps}
                onCheckedChange={(checked) => setIsMaxReps(checked === true)}
              />
              <Label htmlFor="movement_max_reps" className={`cursor-pointer ${isMaxReps ? 'font-semibold' : ''}`}>
                {t('wods.movementDialog.maxReps')}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">{t('wods.movementDialog.maxRepsHelp')}</p>
          </div>

          {/* Charge prescrite : kg, ou % du 1RM (jamais convertis) */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="movement_weight">{t('wods.movementDialog.weightKg')}</Label>
              <NumberInput
                id="movement_weight"
                value={weightKg}
                onChange={(e) => setWeightKg(e.target.value === '' ? '' : Number(e.target.value))}
                min={0}
                step={0.5}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="movement_percent_rm">{t('wods.movementDialog.percentRm')}</Label>
              <NumberInput
                id="movement_percent_rm"
                value={percentageRm}
                onChange={(e) => setPercentageRm(e.target.value === '' ? '' : Number(e.target.value))}
                className={errors.percentageRm ? 'border-destructive' : ''}
                min={1}
                max={200}
                step={1}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="movement_rm_reference">{t('wods.movementDialog.rmReference')}</Label>
              <Input
                id="movement_rm_reference"
                value={rmReference}
                onChange={(e) => setRmReference(e.target.value)}
                disabled={percentageRm === ''}
                placeholder="Deadlift"
              />
            </div>
          </div>
          <p className={`text-xs ${errors.percentageRm ? 'text-destructive' : 'text-muted-foreground'}`}>
            {errors.percentageRm || t('wods.movementDialog.percentRmHelp')}
          </p>

          {/* Notes (poids, modifications, etc.) */}
          <div className="space-y-2">
            <Label htmlFor="movement_notes">{t('wods.movementDialog.notes')}</Label>
            <Textarea
              id="movement_notes"
              placeholder={t('wods.movementDialog.notesPlaceholder')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
            <p className="text-xs text-muted-foreground">
              {t('wods.movementDialog.notesHelp')}
            </p>
          </div>

          {/* Checkbox Tiebreak */}
          {!disableTiebreak && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="movement_tiebreak"
                      checked={isTiebreak}
                      onCheckedChange={(checked) => setIsTiebreak(checked === true)}
                    />
                    <Label
                      htmlFor="movement_tiebreak"
                      className={`cursor-pointer ${isTiebreak ? 'font-semibold' : ''}`}
                    >
                      {t('wods.movementDialog.tiebreak')}
                    </Label>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('wods.movementDialog.tiebreakHelp')}</p>
                </TooltipContent>
              </Tooltip>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {t('wods.movementDialog.cancel')}
          </Button>
          <Button onClick={handleSave}>
            {movement ? t('wods.movementDialog.update') : t('wods.movementDialog.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
